/* Nova — the screen-aware agent panel (text). See [[nova-screen-aware-agent]].

   Turns are pure Firestore: sending a message CREATES a queued aiMessage
   doc stamped with the route, the project/document under the cursor and a
   redacted outline of what is on screen (utils/screenOutline.js); the Nova
   worker (backend/api/agentosWorker.js) claims and drives it — triage →
   answer with read tools | ask | plan behind the approval gate — and the
   stage line, live plan card, step lights and approval buttons below are
   just onSnapshot rendering the same doc. No sockets, no REST ask.

   Hands: an assistant message may carry `navigateTo` (the panel routes and
   folds away so it does not cover the page it opened) and `pageControls`
   (run in order on the screen — utils/pageControls.js), plus `suggestions`
   chips (navigate / act / ask) and `sources` (project-brain citations that
   deep-link to clauses).

   Members only (the aiSession rules are per-user). Renders nothing unless
   REACT_APP_UPLOAD_API is configured. */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useNovaTurn, { NOVA_OPEN_EVENT, NOVA_CLOSE_EVENT, announceNovaState, routeContext } from '../hooks/useNovaTurn';
import { runPageControl } from '../utils/pageControls';
import { openCommandBar } from './CommandBar';
import '../utils/pageControls.css';
import './ChatAgent.css';

const API = process.env.REACT_APP_UPLOAD_API || '';

const STEP_DOT = { pending: 'var(--muted)', running: 'var(--warn, #f59e0b)', done: 'var(--ok, #34d399)', error: 'var(--danger, #f87171)' };

// Tool names in plain words for the plan card.
const humanTool = (t) => String(t || '').replace(/_/g, ' ');

function SourceChip({ dealId, source }) {
  const srcDeal = source.dealId || dealId;
  const cross = source.dealId && source.dealId !== dealId;
  const label = `[${source.n}]${cross && source.dealName ? ` ${source.dealName} —` : ''} ${source.heading || source.docTitle || 'source'}`;
  if (source.docId && srcDeal) {
    return (
      <Link
        className="chat-cite chat-cite-link"
        to={`/deals/${srcDeal}/docs/${source.docId}${source.clauseId ? `?clause=${source.clauseId}` : ''}`}
        title={source.docTitle || source.docId}
      >
        {label}
      </Link>
    );
  }
  return <span className="chat-cite" title={source.snippet}>{label}</span>;
}

function StepRow({ step }) {
  return (
    <>
      <div className="chat-step">
        <span className={`chat-dot${step.status === 'running' ? ' chat-dot-pulse' : ''}`} style={{ background: STEP_DOT[step.status] || STEP_DOT.pending }} />
        <span className="chat-step-tool">{humanTool(step.tool)}</span>
        {step.note && <span className="chat-step-note">{step.note}</span>}
        {typeof step.ms === 'number' && <span className="chat-step-ms">{(step.ms / 1000).toFixed(1)}s</span>}
      </div>
      {step.error && <div className="chat-step-err">{step.error}</div>}
    </>
  );
}

function PlanCard({ msg, onApprove }) {
  const plan = msg.plan;
  if (!plan) return null;
  const waiting = msg.status === 'awaiting_approval';
  return (
    <div className={`chat-plan${waiting ? ' chat-plan-waiting' : ''}`} data-nova-ignore>
      <div className="chat-plan-title">
        {plan.title || 'Plan'}
        {plan.replanned ? <span className="chat-plan-replan"> · revised ×{plan.replanned}</span> : null}
      </div>
      {plan.rationale && <div className="chat-plan-rationale">{plan.rationale}</div>}
      <div>{(plan.steps || []).map((s) => <StepRow key={s.id} step={s} />)}</div>
      {waiting && (
        <div className="chat-plan-actions">
          <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={() => onApprove('approved')}>Approve &amp; run</button>
          <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => onApprove('rejected')}>Reject</button>
        </div>
      )}
    </div>
  );
}

export default function ChatAgent() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [navPeek, setNavPeek] = useState(false);
  const [input, setInput] = useState('');
  const { me, messages, busy, error: sendErr, setError: setSendErr, enqueue, approve: approveTurn, newSession } = useNovaTurn({ enabled: open });
  const logRef = useRef(null);
  const mountedAtRef = useRef(Date.now());
  const executedRef = useRef(new Set());
  const { dealId } = routeContext(useLocation());

  // Other surfaces (command bar, guide, voice) open Nova with an optional
  // prefilled question.
  useEffect(() => {
    const onOpen = (e) => { setNavPeek(false); setOpen(true); if (e?.detail?.ask) setInput(String(e.detail.ask)); };
    const onClose = () => setOpen(false);
    window.addEventListener(NOVA_OPEN_EVENT, onOpen);
    window.addEventListener(NOVA_CLOSE_EVENT, onClose);
    return () => {
      window.removeEventListener(NOVA_OPEN_EVENT, onOpen);
      window.removeEventListener(NOVA_CLOSE_EVENT, onClose);
    };
  }, []);
  useEffect(() => { announceNovaState({ open, busy: open && busy, navPeek: navPeek && !open }); }, [open, busy, navPeek]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, busy]);

  // The worker's hands: fresh assistant messages carrying navigateTo route
  // the app (the panel folds so it doesn't cover the page), ones carrying
  // pageControls run them in order (once per message; history never replays).
  useEffect(() => {
    for (const m of messages || []) {
      if (m.role !== 'assistant' || (!m.navigateTo && !m.pageControls?.length)) continue;
      if (m.createdAt < mountedAtRef.current || executedRef.current.has(m.id)) continue;
      executedRef.current.add(m.id);
      let wait = 0;
      if (m.navigateTo && String(m.navigateTo).startsWith('/')) {
        setOpen((o) => { if (o) setNavPeek(true); return false; });
        navigate(m.navigateTo);
        wait = 600;
      }
      for (const pc of (Array.isArray(m.pageControls) ? m.pageControls : []).slice(0, 6)) {
        setTimeout(() => { try { runPageControl(pc.tool || pc.name, pc.params || pc.args || {}); } catch (_) { /* narrated next turn */ } }, wait);
        wait += 900;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const send = async (text) => {
    const q = String(text ?? input).trim();
    if (!q || busy) return;
    setInput('');
    try {
      await enqueue(q);
    } catch (err) {
      setSendErr(err.message);
    }
  };

  const onSuggestion = (s) => {
    if (s.kind === 'navigate') { setOpen(false); setNavPeek(true); navigate(s.value); return; }
    send(s.value);
  };

  const approve = async (decision, id) => {
    try { await approveTurn(decision, id); } catch (err) { setSendErr(err.message); }
  };

  if (!API || !me) return null;

  const lastUser = [...(messages || [])].reverse().find((m) => m.role === 'user');
  const stage = lastUser && ['queued', 'planning', 'executing'].includes(lastUser.status) ? (lastUser.stage || 'thinking') : '';
  const lastAssistantIdx = (messages || []).map((m) => m.role).lastIndexOf('assistant');

  return (
    <div className="chat-agent" data-nova-ignore>
      {open && (
        <div className="chat-panel" role="dialog" aria-label="Nova">
          <div className="chat-head">
            <span>Nova{dealId ? ' · this project' : ''}</span>
            <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'center' }}>
              <button type="button" className="chat-close" title="New conversation" onClick={() => newSession().catch((e) => setSendErr(e.message))}>＋</button>
              <button type="button" className="chat-close" onClick={() => setOpen(false)} aria-label="Close">✕</button>
            </span>
          </div>
          <div className="chat-log" ref={logRef}>
            {(messages || []).length === 0 && (
              <div className="chat-empty">
                I can see the screen you're on. Ask what something means, where to find a thing, what a project's documents say, or tell me to do it — accepting a point, filing a memo, opening a tab. Anything that changes a record shows you a plan to approve first.
              </div>
            )}
            {(messages || []).map((m, i) => (
              <div key={m.id} className={`chat-msg chat-${m.role === 'user' ? 'user' : 'agent'}`}>
                <div className="chat-text">{m.content}</div>
                {m.role === 'user' && m.plan && <PlanCard msg={m} onApprove={(d) => approve(d, m.id)} />}
                {m.role === 'user' && m.status === 'error' && m.error && <div className="chat-note chat-err">{m.error}</div>}
                {m.sources?.length > 0 && (
                  <div className="chat-citations">
                    {m.sources.map((s, j) => <SourceChip key={`${s.n || j}`} dealId={dealId} source={s} />)}
                  </div>
                )}
                {m.role === 'assistant' && i === lastAssistantIdx && (m.suggestions?.length > 0 || dealId) && (
                  <div className="chat-citations">
                    {(m.suggestions || []).map((s, j) => (
                      <button key={j} type="button" className="chat-cite chat-cite-link" onClick={() => onSuggestion(s)} title={s.kind === 'navigate' ? s.value : s.kind === 'act' ? 'Nova will plan this and ask you to approve' : 'Ask this'}>
                        {s.kind === 'navigate' ? '→ ' : s.kind === 'act' ? '⚡ ' : ''}{s.label}
                      </button>
                    ))}
                    {dealId && (
                      <button type="button" className="chat-cite chat-cite-link" onClick={() => openCommandBar({ verb: 'do', instruction: '' })} title="Open the project command bar">
                        Command bar
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
            {stage && (
              <div className="chat-msg chat-agent">
                <div className="chat-text chat-thinking">{stage}…</div>
              </div>
            )}
            {sendErr && <div className="chat-note chat-err">{sendErr}</div>}
          </div>
          <form className="chat-input-row" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={dealId ? 'Ask about this project, or tell me what to do…' : 'Ask Nova…'}
              disabled={busy}
              aria-label="Message Nova"
            />
            <button className="chat-send" type="submit" disabled={busy || !input.trim()} aria-label="Send">→</button>
          </form>
        </div>
      )}
      {!open && (
        <button
          className={`chat-launcher${navPeek ? ' chat-launcher-peek' : ''}`}
          title="Nova — ask about this screen or tell me what to do"
          onClick={() => { setNavPeek(false); setOpen(true); }}
          aria-label="Open Nova"
        >
          {navPeek ? '↩' : '✦'}
        </button>
      )}
    </div>
  );
}
